import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { createFormGroup, updateFormGroup, type FormGroup } from '../api/form-builder-api'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import { FORM_GROUP_ICONS, DEFAULT_GROUP_ICON } from '@/lib/utils/lucide-icon-catalog'
import { IconPicker } from '@/components/shared/icon-picker'

const schema = z.object({
  title: z.string().min(1, 'عنوان الزامی است'),
  description: z.string().optional(),
  icon: z.string().default(DEFAULT_GROUP_ICON),
})

type FormData = z.infer<typeof schema>

interface FormGroupModalProps {
  projectId: string
  group?: FormGroup | null
  open: boolean
  onClose: () => void
}

export function FormGroupModal({ projectId, group, open, onClose }: FormGroupModalProps) {
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { title: '', description: '', icon: DEFAULT_GROUP_ICON },
  })

  useEffect(() => {
    if (group) {
      form.reset({
        title: group.title,
        description: group.description,
        icon: group.icon && FORM_GROUP_ICONS.includes(group.icon) ? group.icon : DEFAULT_GROUP_ICON,
      })
    } else {
      form.reset({ title: '', description: '', icon: DEFAULT_GROUP_ICON })
    }
  }, [group, open])

  const mutation = useMutation({
    mutationFn: (data: FormData) =>
      group
        ? updateFormGroup(group.id, user!.id, data)
        : createFormGroup(projectId, user!.id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['form-groups', projectId] })
      onClose()
    },
  })

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{group ? 'ویرایش گروه فرم' : 'ایجاد گروه فرم'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit((d) => mutation.mutate(d))} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>عنوان گروه</FormLabel>
                  <FormControl>
                    <Input placeholder="مثال: اطلاعات پایه" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>توضیحات (اختیاری)</FormLabel>
                  <FormControl>
                    <Textarea rows={2} placeholder="توضیح مختصر..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="icon"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>آیکون</FormLabel>
                  <IconPicker
                    value={field.value}
                    onChange={(key) => field.onChange(key || DEFAULT_GROUP_ICON)}
                    icons={FORM_GROUP_ICONS}
                    clearable={false}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>انصراف</Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'در حال ذخیره...' : group ? 'ذخیره تغییرات' : 'ایجاد گروه'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
